import React, { useState } from 'react';
import { getWrestlerName, wrestlerMatchesSearch } from './wrestlerUtils';
import UnifiedWrestlerCard, { WrestlerCardStyles } from './WrestlerCard';
import { getClassIcon } from './wrestlerIcons';
import { hasSynergyWithGroup } from './BookingPage';

// ── Helpers ──────────────────────────────────────────────────────────

const getTitleRender = (titleId) => {
  if (!titleId) return null;
  return process.env.PUBLIC_URL + '/titles/' + titleId + '-render.png';
};

const isMatchSpecialist = (wrestler, matchTypeId) => {
  if (!matchTypeId || matchTypeId === 'normal') return false;
  var specialties = wrestler.specialties || wrestler.match_specialties || [];
  return specialties.includes(matchTypeId);
};

const RIVALRY_LVL_COLORS = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#9b59b6' };

const S_INPUT = { background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '10px 14px', color: '#fff', fontSize: '0.65rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '100%', colorScheme: 'dark' };

// ── SuperstarPicker ───────────────────────────────────────────────────
// Props:
//   roster          — full roster array
//   alreadyPicked   — array of wrestler objects already selected (excluded)
//   onSelect        — callback(wrestler) when a card is clicked
//   onClose         — callback to close the picker
//   champions       — { slots, champions } object (optional)
//   matchBookedIds  — array of wrestler_ids already booked this week (optional)
//   isTarget        — bool, if true bypasses some filters (optional)
//   promoAction     — string promo action type (optional)
//   rivalries       — array of rivalry objects (optional)
//   matchType       — current match type id (optional)
//   title           — picker header title (optional, default "SELECT SUPERSTAR")
//   filterActive    — if true, only show active wrestlers (default: true)

const SuperstarPicker = ({
  roster,
  alreadyPicked = [],
  onSelect,
  onClose,
  champions,
  matchBookedIds,
  isTarget = false,
  promoAction,
  rivalries,
  matchType,
  title = 'SELECT SUPERSTAR',
  filterActive = true,
  autoClose = true,
  showSynergy = true,
  defaultGender = 'ALL',
  selectedIds = [],
  showConfirm = false,
  onConfirm = null,
  confirmDisabled = false,
  // Inter-brand PLE: array of { brand: string, color: string, logo: string }
  // When provided, a "RIVAL BRANDS" strip is shown above the superstar grid.
  // Clicking a brand card calls onSelect with a brand-participant sentinel object.
  rivalBrands = null,
  // Optional button injected in the footer left, next to CANCEL (e.g. "CHANGE TITLE")
  onChangeTitle = null,
}) => {
  const [search, setSearch]             = useState('');
  const [filterGender, setFilterGender] = useState(defaultGender);
  const [filterRole, setFilterRole]     = useState('ALL');
  const [filterChamp, setFilterChamp]   = useState(false);
  const [filterRivalry, setFilterRivalry] = useState(false);
  const [sortAZ, setSortAZ]             = useState(true);

  // Build champion map
  var champMap = {};
  var champSlots = (champions && champions.slots) || [];
  var champData  = (champions && champions.champions) || {};
  champSlots.forEach(function(slot) {
    var holders = champData[slot.key] || [];
    holders.forEach(function(h) {
      if (h && h.wrestler_id) {
        champMap[h.wrestler_id] = {
          titleRender: (slot.titleOverride && (slot.titleOverride.render || getTitleRender(slot.titleOverride.id))) || null,
          titleName:   (slot.titleOverride && slot.titleOverride.name) || slot.key,
        };
      }
    });
  });

  // Rivalry lookup
  var getRivalry = function(w) {
    var wid = String(w.wrestler_id);
    return (rivalries || []).find(function(r) {
      return (r.side1 || []).some(function(id) { return String(id) === wid; }) ||
             (r.side2 || []).some(function(id) { return String(id) === wid; });
    }) || null;
  };

  // Call-out targets (solo or tag) are allowed to be injured/suspended/absent
  // (you can call out someone who's out — that's the storyline point)
  var isCallOutTarget = isTarget && (promoAction === 'call-out' || promoAction === 'tag-team-call-out');

  var available = roster.filter(function(w) {
    if (w.status === 'gm') return false;
    if (filterActive && w.status !== 'active') return false;
    if (alreadyPicked.find(function(p) { return p && p.wrestler_id === w.wrestler_id; })) return false;
    if (!wrestlerMatchesSearch(w, search)) return false;
    if (!isTarget && matchBookedIds && matchBookedIds.indexOf(w.wrestler_id) !== -1) return false;
    if (filterGender === 'MALE'   && w.gender !== 'M') return false;
    if (filterGender === 'FEMALE' && w.gender !== 'F') return false;
    if (filterChamp   && !champMap[w.wrestler_id])    return false;
    if (filterRivalry && !getRivalry(w))              return false;
    if (promoAction === 'class-change' && !w.fight_class_secondary) return false;
    if (filterRole === 'FACE' &&  w.heel) return false;
    if (filterRole === 'HEEL' && !w.heel) return false;
    // Exclude unavailable superstars unless it's a call-out target
    if (!isCallOutTarget && (w.injured && w.injuryWeeksLeft > 0)) return false;
    if (!isCallOutTarget && (w.suspended && w.suspensionWeeksLeft > 0)) return false;
    if (!isCallOutTarget && w.absent) return false;
    return true;
  });

  available = available.slice().sort(function(a, b) {
    var na = getWrestlerName(a, 'display');
    var nb = getWrestlerName(b, 'display');
    return sortAZ ? na.localeCompare(nb) : nb.localeCompare(na);
  });

  var btnStyle = function(active, color) {
    return {
      fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1px',
      background:   active ? (color ? color + '22' : 'rgba(255,255,255,0.12)') : 'transparent',
      border: '1px solid ' + (active ? (color || 'rgba(255,255,255,0.5)') : 'rgba(255,255,255,0.15)'),
      color:        active ? (color || '#fff') : 'rgba(255,255,255,0.4)',
      padding: '4px 12px', borderRadius: '4px', cursor: 'pointer',
      transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    };
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.88)' }}
      onClick={onClose}>
      <style>{WrestlerCardStyles}</style>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(8,8,8,0.97)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', width: 'min(95vw, 1100px)', height: '75vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={function(e) { e.stopPropagation(); }}>

        {/* Header */}
        <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', letterSpacing: '3px', color: '#fff' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>✕</button>
        </div>

        {/* Search + Sort */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0, background: 'rgba(0,0,0,0.3)' }}>
          <input style={{ ...S_INPUT, flex: 1 }} placeholder="Search..." value={search}
            autoFocus onChange={function(e) { setSearch(e.target.value); }} />
          <button onClick={function() { setSortAZ(function(p) { return !p; }); }}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}>
            SORT {sortAZ ? 'A → Z ↑' : 'Z → A ↓'}
          </button>
        </div>

        {/* Filters */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          {['ALL', 'MALE', 'FEMALE'].map(function(g) {
            return <button key={g} onClick={function() { setFilterGender(g); }} style={btnStyle(filterGender === g)}>{g}</button>;
          })}
          <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.1)', margin: '0 2px' }} />
          {['ALL', 'FACE', 'HEEL'].map(function(r) {
            return <button key={r} onClick={function() { setFilterRole(r); }} style={btnStyle(filterRole === r)}>{r}</button>;
          })}
          <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.1)', margin: '0 2px' }} />
          {champions && (
            <button onClick={function() { setFilterChamp(function(p) { return !p; }); setFilterRivalry(false); }}
              style={btnStyle(filterChamp, '#d4af37')}>👑 CHAMPIONS</button>
          )}
          {rivalries && rivalries.length > 0 && (
            <button onClick={function() { setFilterRivalry(function(p) { return !p; }); setFilterChamp(false); }}
              style={btnStyle(filterRivalry, '#e74c3c')}>⚔ RIVALRY</button>
          )}
          <div style={{ marginLeft: 'auto', fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)' }}>
            {available.length} superstars
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '0' }}>

          {/* Rival brands strip — PLE inter-brand only */}
          {rivalBrands && rivalBrands.length > 0 && (
            <div style={{ marginBottom: '16px', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.28)', marginBottom: '8px' }}>RIVAL BRANDS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {rivalBrands.map(function(rb) {
                  return (
                    <div key={rb.brand + '-' + Math.random()} onClick={function() {
                        onSelect({ isBrand: true, brand_id: rb.brand, wrestler_id: '__brand__' + rb.brand + '-' + Date.now(), name: rb.brand, brandColor: rb.color, brandLogo: rb.logo, brandBg: rb.bg });
                        if (autoClose) onClose();
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '6px', border: '1px solid ' + rb.color + '55', background: rb.color + '14', cursor: 'pointer', transition: 'background 0.12s, border-color 0.12s' }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = rb.color + '28'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = rb.color + '14'; }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: rb.color, flexShrink: 0 }} />
                      {rb.logo && (
                        <div style={{ width: '56px', height: '28px', background: rb.color + '1a', border: '1px solid ' + rb.color + '44', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <img loading="lazy" src={rb.logo} alt={rb.brand} style={{ height: '18px', objectFit: 'contain' }} onError={function(e) { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                      <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', color: '#fff', letterSpacing: '0.5px' }}>{rb.brand}</span>
                      <span style={{ marginLeft: 'auto', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', background: rb.color + '18', border: '1px solid ' + rb.color + '44', color: rb.color, padding: '2px 8px', borderRadius: '3px' }}>BRAND</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '14px 0 4px' }} />
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.28)', marginBottom: '10px' }}>SUPERSTARS</div>
            </div>
          )}

          {/* Superstar cards grid */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignContent: 'flex-start', justifyContent: 'center' }}>
          {available.map(function(w, i) {
            var isPromoMode = !!(isTarget || promoAction);
            var pickerHasSynergy = (showSynergy && !isPromoMode) ? hasSynergyWithGroup(w, alreadyPicked || []) : false;
            var pickerIsSpecialist  = isMatchSpecialist(w, matchType);
            var isSelected = selectedIds.includes(w.wrestler_id);
            return (
              <div key={w.wrestler_id + '-' + i} style={{ position: 'relative' }}>
                {isSelected && (
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '8px', border: '2px solid #d4af37', zIndex: 10, pointerEvents: 'none', boxShadow: '0 0 12px rgba(212,175,55,0.4)' }} />
                )}
                <UnifiedWrestlerCard
                  wrestler={w}
                  variant="match"
                  rivalries={rivalries}
                  champions={champions}
                  roster={roster}
                  hasSynergy={pickerHasSynergy}
                  isMatchSpecialist={pickerIsSpecialist}
                  matchType={matchType}
                  onClick={function() { onSelect(w); if (autoClose) onClose(); }}
                />
              </div>
            );
          })}
          {available.length === 0 && (
            <div style={{ width: '100%', textAlign: 'center', padding: '60px 20px', fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>
              No superstars available.
            </div>
          )}
          </div>
        </div>

        {/* Footer CONFIRM — optionnel */}
        {showConfirm && (
          <div style={{ padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={onClose} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
              {onChangeTitle && (
                <button onClick={onChangeTitle} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.35)', color: 'rgba(212,175,55,0.7)', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>🏆 CHANGE TITLE</button>
              )}
            </div>
            <button onClick={onConfirm} disabled={confirmDisabled}
              style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '2px', background: confirmDisabled ? 'transparent' : 'rgba(212,175,55,0.1)', border: '1px solid ' + (confirmDisabled ? 'rgba(255,255,255,0.1)' : 'rgba(212,175,55,0.5)'), color: confirmDisabled ? 'rgba(255,255,255,0.2)' : '#d4af37', padding: '9px 28px', borderRadius: '4px', cursor: confirmDisabled ? 'not-allowed' : 'pointer' }}>
              ✓ CONFIRM
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default SuperstarPicker;
